import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CAMPO } from "../componentes/ui";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import {
  ACEITA_ANEXO,
  FORMAS,
  MESES,
  NATUREZAS,
  SETORES,
  anexarComprovante,
  atualizarCliente,
  atualizarLancamento,
  cadastrarCliente,
  convidarCliente,
  excluirLancamento,
  extrato,
  faturamento,
  lancar,
  listarClientes,
  motivoDoTeto,
  removerCliente,
  removerComprovante,
  urlDoComprovante,
  type ClienteMei,
  type DadosDoLancamento,
  type FaturamentoAno,
  type Lancamento,
  type Natureza,
  type ResultadoDoConvite,
} from "../lib/meipro";
import {
  emailValido,
  mascararCnpj,
  mascararMoeda,
  moedaDeNumero,
  soDigitos,
} from "../lib/mascaras";

// A gestão de clientes MEI — a metade transacional do MEI Pro (000077).
//
// No Bubble isto é o menu "Ferramentas": Gerenciar clientes, Livro-caixa e
// Controle de Faturamento. Aqui são DUAS telas, porque o uso real é "abrir o
// cliente e trabalhar no livro dele" — o faturamento é um painel DENTRO do
// livro, não uma tela à parte que obrigaria a reescolher o cliente.
//
//   /ferramenta/mei-pro/gestao                a lista de clientes + cadastro
//   /ferramenta/mei-pro/gestao/<cliente_id>   o livro-caixa + faturamento
//
// Esta tela NÃO decide acesso: toda leitura e escrita passa pelas RPCs, que
// perguntam a `mei_posso_escriturar` (contador vinculado com o card aberto, ou
// o próprio cliente). Se as chamadas voltarem vazias/recusadas, a tela diz isso.
//
// O VALOR do lançamento anda separado do texto do campo (`mascararMoeda`
// devolve os dois) e só o número viaja — a cicatriz do campo de moeda do
// orçamento, aplicada aqui desde o primeiro dia.

export default function MeiGestao() {
  const { "*": resto = "" } = useParams();
  // resto = "gestao" ou "gestao/<cliente_id>" — o MeiPro só nos monta nesses.
  const clienteId = resto.split("/").filter(Boolean)[1] ?? null;

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      {clienteId ? <Livro clienteId={clienteId} /> : <Clientes />}
    </main>
  );
}

// ---------------------------------------------------------------------------
// A lista de clientes
// ---------------------------------------------------------------------------
function Clientes() {
  const [clientes, setClientes] = useState<ClienteMei[] | null>(null);
  const [erro, setErro] = useState(false);
  const [cadastrando, setCadastrando] = useState(false);
  // O cliente do popup de convite. Abre em DOIS momentos: logo depois do
  // cadastro (o pedido de Victor: "quer que esse cliente tenha acesso?") e
  // pelo "Convidar por e-mail" da linha, para quem disse "agora não" e mudou
  // de ideia — sem o segundo caminho, o "não" do popup seria definitivo.
  const [convite, setConvite] = useState<ClienteMei | null>(null);

  const carregar = useCallback(async () => {
    const r = await listarClientes();
    if (r === null) setErro(true);
    else setClientes(r);
    return r;
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro) {
    return (
      <Cabecalho titulo="Gestão de clientes MEI">
        <p className="mt-8 rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar os seus clientes agora. Recarregue a página; se
          continuar, fale com o suporte.
        </p>
      </Cabecalho>
    );
  }
  if (!clientes) return <Carregando />;

  return (
    <Cabecalho
      titulo="Gestão de clientes MEI"
      rotulo="Carteira"
      acao={
        <button type="button" className="pill-cta text-sm" onClick={() => setCadastrando((v) => !v)}>
          {cadastrando ? "Fechar" : "+ Cadastrar cliente"}
        </button>
      }
      // Os três fatos da carteira, medidos nos dados que a lista já trouxe: nada
      // é chamada a mais. "Acima de 80%" é o degrau que o conteúdo do MEI ensina
      // ("Ultrapassou o limite?"), e é o número que decide o que o contador faz
      // hoje. Carteira vazia não mostra fato nenhum — zero grande é ruído.
      fatos={
        clientes.length > 0 ? (
          <>
            <TileDeFato
              rotulo="Clientes"
              valor={clientes.length}
              apoio={clientes.length === 1 ? "MEI na carteira" : "MEIs na carteira"}
            />
            <TileDeFato
              rotulo="Faturado no ano"
              valor={moedaDeNumero(clientes.reduce((s, c) => s + c.receita_no_ano, 0))}
              apoio="soma dos livros"
            />
            {(() => {
              const acima = clientes.filter((c) => c.limite > 0 && c.receita_no_ano / c.limite >= 0.8).length;
              return (
                <TileDeFato
                  rotulo="Acima de 80% do teto"
                  valor={acima}
                  apoio={acima === 0 ? "ninguém perto do limite" : "merecem atenção"}
                />
              );
            })()}
          </>
        ) : undefined
      }
    >
      {cadastrando && (
        <FormularioDeCliente
          aoSalvar={async (email) => {
            setCadastrando(false);
            const lista = await carregar();
            // O cliente recém-cadastrado, achado pelo e-mail na lista RECARREGADA
            // (o cadastro devolve o vínculo, não a linha completa). Quem já tem
            // login não recebe popup — não há convite a fazer.
            const novo = email ? lista?.find((c) => c.email === email) : undefined;
            if (novo && !novo.tem_login) setConvite(novo);
          }}
        />
      )}

      {convite && <DialogoDeConvite cliente={convite} aoFechar={() => { setConvite(null); void carregar(); }} />}

      {clientes.length === 0 && !cadastrando ? (
        <div className="surface mt-4 p-8 text-center">
          <p className="text-sm leading-relaxed text-cinza">
            Cadastre o seu primeiro cliente MEI para abrir o livro-caixa dele e
            acompanhar o faturamento contra o teto do ano.
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {clientes.map((c) => (
            <LinhaDeCliente key={c.id} cliente={c} aoMudar={carregar} aoConvidar={() => setConvite(c)} />
          ))}
        </ul>
      )}
    </Cabecalho>
  );
}

function LinhaDeCliente({
  cliente,
  aoMudar,
  aoConvidar,
}: {
  cliente: ClienteMei;
  aoMudar: () => void;
  aoConvidar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  return (
    <li className="surface p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-base font-bold text-titulo">
            {cliente.nome ?? "Sem nome"}
          </p>
          <p className="mt-0.5 text-sm text-cinza">
            {cliente.email}
            {cliente.cnpj ? ` · CNPJ ${mascararCnpj(cliente.cnpj)}` : " · sem CNPJ"}
          </p>
        </div>
        <Link
          to={`/ferramenta/mei-pro/gestao/${cliente.cliente_id}`}
          className="pill-cta shrink-0"
        >
          Abrir livro-caixa
        </Link>
      </div>

      <BarraDoTeto
        rotulo="Faturamento no ano"
        valor={cliente.receita_no_ano}
        teto={cliente.limite}
        // Os três sinais vêm PRONTOS de quem calculou o teto (000091). A
        // versão anterior cruzava "é proporcional?" (agregado) com a abertura
        // do MEU vínculo, e nos clientes de dois contadores a frase desaparecia
        // exatamente quando o outro contador havia declarado o fato.
        motivo={motivoDoTeto({
          manual: cliente.teto_manual,
          caminhoneiro: cliente.teto_caminhoneiro,
          mesDeAberturaProporcional: cliente.teto_mes_abertura,
        })}
      />

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-cinza">
        <span className="tabular-nums">
          {cliente.lancamentos} {cliente.lancamentos === 1 ? "lançamento" : "lançamentos"}
        </span>
        {cliente.ultimo_lancamento && (
          <span>último em {dataCurta(cliente.ultimo_lancamento)}</span>
        )}
        <span aria-hidden className="text-borda">·</span>
        {cliente.tem_login ? (
          <span>já tem acesso à plataforma</span>
        ) : (
          <>
            <button type="button" className="font-semibold text-link" onClick={aoConvidar}>
              Convidar por e-mail
            </button>
            {cliente.convidado_em && (
              <span>convite enviado em {dataCurta(cliente.convidado_em.slice(0, 10))}</span>
            )}
          </>
        )}
        <button type="button" className="font-semibold text-link" onClick={() => setEditando((v) => !v)}>
          {editando ? "Fechar edição" : "Editar cliente"}
        </button>
        {removendo ? (
          <span className="inline-flex items-center gap-2">
            <span className="font-semibold text-erro-tinta">
              Tirar {cliente.nome ?? "este cliente"} da sua lista? O livro-caixa dele não é apagado.
            </span>
            <button
              type="button"
              className="font-semibold text-erro-tinta underline"
              onClick={async () => {
                const r = await removerCliente(cliente.id);
                if (!r.ok) setRecado(r.mensagem);
                else aoMudar();
              }}
            >
              Sim, tirar
            </button>
            <button type="button" className="font-semibold text-link" onClick={() => setRemovendo(false)}>
              Não
            </button>
          </span>
        ) : (
          <button type="button" className="font-semibold text-erro-tinta" onClick={() => setRemovendo(true)}>
            Remover da lista
          </button>
        )}
      </div>

      {recado && <p className="mt-2 text-sm font-semibold text-erro-tinta">{recado}</p>}

      {editando && (
        <FormularioDeCliente
          cliente={cliente}
          aoSalvar={() => {
            setEditando(false);
            aoMudar();
          }}
        />
      )}
    </li>
  );
}

/** Cadastro e edição no MESMO formulário — os campos são os mesmos. */
function FormularioDeCliente({
  cliente,
  aoSalvar,
}: {
  cliente?: ClienteMei;
  /** No cadastro, recebe o e-mail do cliente novo — é ele que abre o popup do convite. */
  aoSalvar: (emailDoNovo?: string) => void;
}) {
  const [nome, setNome] = useState(cliente?.nome ?? "");
  const [email, setEmail] = useState(cliente?.email ?? "");
  const [cnpj, setCnpj] = useState(cliente?.cnpj ? mascararCnpj(cliente.cnpj) : "");
  const [caminhoneiro, setCaminhoneiro] = useState(cliente?.caminhoneiro ?? false);
  // O ano e o mês da abertura, separados: é assim que o contador sabe o dado
  // ("abriu em março") — dia exato de abertura ele não tem, e pedir uma data
  // completa faria a tela cobrar precisão que não existe.
  const [abriuNoAno, setAbriuNoAno] = useState(Boolean(cliente?.abertura));
  const [anoAbertura, setAnoAbertura] = useState(
    cliente?.abertura ? cliente.abertura.slice(0, 4) : String(new Date().getFullYear()),
  );
  const [mesAbertura, setMesAbertura] = useState(
    cliente?.abertura ? String(Number(cliente.abertura.slice(5, 7))) : "1",
  );
  const [recado, setRecado] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const editando = Boolean(cliente);
  const anoCorrente = new Date().getFullYear();

  return (
    <form
      className="mt-4 space-y-3 rounded-xl bg-superficie-2 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setRecado(null);
        if (!nome.trim()) return setRecado("O cliente precisa de um nome.");
        if (!editando && !emailValido(email)) return setRecado("Confira o e-mail do cliente.");
        const digitos = soDigitos(cnpj);
        if (digitos !== "" && digitos.length !== 14) {
          return setRecado("O CNPJ precisa dos 14 dígitos — ou deixe em branco.");
        }
        // A abertura vai como o PRIMEIRO DIA do mês escolhido — o dia não é
        // dado que o contador tenha, e a RPC trunca de qualquer jeito.
        const abertura = abriuNoAno
          ? `${anoAbertura}-${String(Number(mesAbertura)).padStart(2, "0")}-01`
          : null;
        setSalvando(true);
        const r = editando
          ? await atualizarCliente(cliente!.id, {
              nome: nome.trim(),
              // string vazia LIMPA o CNPJ; nulo seria "não mexe" (convenção da RPC)
              cnpj: digitos === "" ? "" : digitos,
              caminhoneiro,
              // Data não tem "vazio" que não seja sentinela: desmarcar a caixa
              // manda a flag de limpar, não um nulo (que a RPC lê como
              // "não mexe" e deixaria a abertura antiga em pé).
              abertura,
              limparAbertura: !abriuNoAno,
            })
          : await cadastrarCliente(nome.trim(), email.trim().toLowerCase(), digitos || null, {
              caminhoneiro,
              abertura,
            });
        setSalvando(false);
        if (!r.ok) return setRecado(r.mensagem);
        // No CADASTRO o e-mail sobe junto: é ele que faz o popup do convite
        // achar o cliente recém-criado na lista recarregada.
        aoSalvar(editando ? undefined : email.trim().toLowerCase());
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block sm:col-span-1">
          <span className="mb-1 block text-sm font-semibold text-titulo">Nome</span>
          <input className={CAMPO} value={nome} onChange={(e) => setNome(e.target.value)} />
        </label>
        <label className="block sm:col-span-1">
          <span className="mb-1 block text-sm font-semibold text-titulo">E-mail</span>
          <input
            className={CAMPO}
            type="email"
            value={email}
            disabled={editando}
            onChange={(e) => setEmail(e.target.value)}
          />
          {editando && (
            <span className="mt-1 block text-xs leading-relaxed text-cinza">
              O e-mail identifica a conta do cliente e não muda por aqui.
            </span>
          )}
        </label>
        <label className="block sm:col-span-1">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            CNPJ <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <input
            className={CAMPO}
            inputMode="numeric"
            value={cnpj}
            onChange={(e) => setCnpj(mascararCnpj(e.target.value))}
          />
        </label>
      </div>
      {/* Os dois fatos que mudam o TETO do cliente. Ficam aqui, no cadastro, e
          não numa tela de "configurar limite": o hub não guarda teto por
          cliente — ele guarda o que a lei usa para calculá-lo, e faz a conta
          (`mei_teto`, 000085). Guardar o número calculado é o defeito que o
          sistema antigo tinha: o proporcional de 2023 continua gravado lá,
          mentindo o teto de quem abriu há três anos. */}
      <section
        role="group"
        aria-labelledby={`fatos-do-teto-${cliente?.id ?? "novo"}`}
        className="rounded-xl bg-superficie p-4"
      >
        <h4
          id={`fatos-do-teto-${cliente?.id ?? "novo"}`}
          className="text-sm font-semibold text-titulo"
        >
          O limite de faturamento deste cliente
        </h4>
        <label className="mt-2.5 flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-laranja"
            checked={caminhoneiro}
            onChange={(e) => setCaminhoneiro(e.target.checked)}
          />
          <span className="text-sm leading-relaxed text-tinta">
            É MEI caminhoneiro
            <span className="block text-xs text-cinza">
              transportador autônomo de cargas — o limite anual é maior
            </span>
          </span>
        </label>
        <label className="mt-3 flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-laranja"
            checked={abriuNoAno}
            onChange={(e) => setAbriuNoAno(e.target.checked)}
          />
          <span className="text-sm leading-relaxed text-tinta">
            O MEI abriu neste ano ou em outro recente
            <span className="block text-xs text-cinza">
              no ano da abertura o limite é proporcional aos meses restantes
            </span>
          </span>
        </label>
        {abriuNoAno && (
          <div className="mt-3 flex flex-wrap gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-titulo">Mês da abertura</span>
              <select
                className={CAMPO}
                value={mesAbertura}
                onChange={(e) => setMesAbertura(e.target.value)}
              >
                {MESES.map((m, i) => (
                  <option key={m} value={String(i + 1)}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-titulo">Ano</span>
              <select
                className={CAMPO}
                value={anoAbertura}
                onChange={(e) => setAnoAbertura(e.target.value)}
              >
                {/* Do ano corrente para trás: abertura futura a RPC recusa, e
                    o proporcional de anos antigos só muda o histórico. */}
                {[0, 1, 2, 3].map((n) => (
                  <option key={n} value={String(anoCorrente - n)}>
                    {anoCorrente - n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </section>
      {!editando && (
        <p className="text-xs leading-relaxed text-cinza">
          O cliente ainda não recebe acesso: o cadastro deixa o livro-caixa pronto, e o
          convite para ele entrar chega numa próxima etapa.
        </p>
      )}
      {recado && <p className="text-sm font-semibold text-erro-tinta">{recado}</p>}
      <button type="submit" className="pill-cta" disabled={salvando}>
        {salvando ? "Salvando…" : editando ? "Salvar cliente" : "Cadastrar"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// O livro-caixa de um cliente
// ---------------------------------------------------------------------------
function Livro({ clienteId }: { clienteId: string }) {
  const [cliente, setCliente] = useState<ClienteMei | null | undefined>(undefined);
  const [anos, setAnos] = useState<FaturamentoAno[] | null>(null);
  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [linhas, setLinhas] = useState<Lancamento[] | null>(null);
  const [lancando, setLancando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const [lista, fat, ext] = await Promise.all([
      listarClientes(),
      faturamento(clienteId),
      extrato(clienteId, ano),
    ]);
    setCliente(lista?.find((c) => c.cliente_id === clienteId) ?? null);
    setAnos(fat);
    setLinhas(ext);
  }, [clienteId, ano]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (cliente === undefined) return <Carregando />;

  // A ORDEM importa: se o id da URL for lixo, o extrato falha e `linhas` fica
  // nulo para sempre — testar `linhas` antes de `cliente` deixaria a tela em
  // "carregando" eterno em vez de dizer que o cliente não está na lista.
  if (cliente === null || anos === null || linhas === null) {
    return (
      <Cabecalho titulo="Livro-caixa">
        <p className="mt-8 rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Este cliente não está na sua lista — ele pode ter sido removido, ou o link é
          antigo.{" "}
          <Link to="/ferramenta/mei-pro/gestao" className="font-semibold text-link underline">
            Ver os meus clientes
          </Link>
        </p>
      </Cabecalho>
    );
  }

  const anoCorrente = new Date().getFullYear();
  const doAno = anos.find((a) => a.ano === ano);
  // O seletor de ano lista os anos COM lançamento + o corrente, sempre.
  const opcoesDeAno = [...new Set([anoCorrente, ...anos.map((a) => a.ano)])].sort((a, b) => b - a);

  return (
    <Cabecalho
      titulo={cliente.nome ?? "Livro-caixa"}
      rotulo="Livro-caixa"
      sub={`${cliente.email ?? ""}${cliente.cnpj ? ` · CNPJ ${mascararCnpj(cliente.cnpj)}` : ""}`}
      volta={{ para: "/ferramenta/mei-pro/gestao", rotulo: "Meus clientes" }}
      // Dois fatos do ANO ESCOLHIDO que o painel de faturamento abaixo não repete
      // (ele fala do ano corrente e do teto): quantos lançamentos há neste ano e
      // qual foi o último. Repetir a receita aqui seria dizer a mesma coisa duas
      // vezes na mesma tela — a cicatriz do IR Pro.
      fatos={
        linhas.length > 0 ? (
          <>
            <TileDeFato
              rotulo={`Lançamentos em ${ano}`}
              valor={linhas.length}
              apoio={`${linhas.filter((l) => l.natureza === "entrada").length} de entrada`}
            />
            <TileDeFato
              rotulo="Último lançamento"
              valor={dataCurta([...linhas].sort((a, b) => (a.data < b.data ? 1 : -1))[0].data)}
            />
          </>
        ) : undefined
      }
    >
      {/* O Controle de Faturamento: a conta vem pronta de mei_faturamento — a
          tela pinta, não soma. Acima de 80% aponta o artigo que explica o que
          fazer, porque a resposta JÁ ESTÁ na ferramenta, na metade de conteúdo. */}
      <section aria-labelledby="mei-faturamento" className="surface mt-7 p-4 sm:p-5">
        <h2 id="mei-faturamento" className="font-display text-base font-bold text-titulo">
          Faturamento × teto do MEI
        </h2>
        {(() => {
          const doCorrente = anos.find((a) => a.ano === anoCorrente);
          const receita = doCorrente?.receita ?? 0;
          const teto = doCorrente?.limite ?? cliente.limite;
          const pct = teto > 0 ? (receita / teto) * 100 : 0;
          return (
            <>
              <BarraDoTeto
                rotulo={`Receita em ${anoCorrente}`}
                valor={receita}
                teto={teto}
                // O porquê vem da LINHA do ano (000087): olhar um ano antigo não
                // pode dizer "proporcional" se aquele não é o ano da abertura.
                motivo={
                  doCorrente
                    ? motivoDoTeto({
                        manual: doCorrente.teto_manual,
                        caminhoneiro: doCorrente.caminhoneiro,
                        mesDeAberturaProporcional: doCorrente.mes_de_abertura,
                      })
                    : motivoDoTeto({
                        manual: cliente.teto_manual,
                        caminhoneiro: cliente.teto_caminhoneiro,
                        mesDeAberturaProporcional: cliente.teto_mes_abertura,
                      })
                }
              />
              {pct >= 80 && (
                <p className="mt-2 text-sm leading-relaxed text-alerta-tinta">
                  {pct >= 100 ? "O teto do ano foi ultrapassado." : "O faturamento passou de 80% do teto."}{" "}
                  <Link
                    to="/ferramenta/mei-pro/sobre-o-mei/ultrapassou-limite"
                    className="font-semibold underline"
                  >
                    O que fazer quando ultrapassa o limite
                  </Link>
                </p>
              )}
              {doCorrente && (
                <p className="mt-2 text-xs text-cinza tabular-nums">
                  Compras no ano: {moedaDeNumero(doCorrente.compras)} de{" "}
                  {moedaDeNumero(doCorrente.limite_compras)} (80% do teto) · Despesas:{" "}
                  {moedaDeNumero(doCorrente.despesas)} · Retiradas:{" "}
                  {moedaDeNumero(doCorrente.retiradas)}
                </p>
              )}
            </>
          );
        })()}
      </section>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-cinza">
          Ano
          <select
            className={CAMPO + " w-auto"}
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
          >
            {opcoesDeAno.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="pill-cta" onClick={() => setLancando((v) => !v)}>
          {lancando ? "Fechar" : "+ Novo lançamento"}
        </button>
      </div>

      {lancando && (
        <FormularioDeLancamento
          aoSalvar={async (d) => {
            const r = await lancar(clienteId, d);
            return r.ok ? { ok: true, id: r.res } : r;
          }}
          aoConcluir={(a) => {
            setLancando(false);
            setAviso(a ?? null);
            void carregar();
          }}
        />
      )}

      {aviso && <p className="mt-3 text-sm font-semibold text-alerta-tinta">{aviso}</p>}

      {linhas.length === 0 ? (
        <div className="surface mt-4 p-8 text-center">
          <p className="text-sm leading-relaxed text-cinza">
            Nenhum lançamento em {ano}.
            {doAno ? "" : " Escolha outro ano acima, ou registre o primeiro."}
          </p>
        </div>
      ) : (
        <ExtratoPorMes linhas={linhas} aoMudar={carregar} />
      )}
    </Cabecalho>
  );
}

/**
 * O extrato agrupado por MÊS, com a rubrica dizendo o mês uma vez e o total de
 * entradas dele (redesenho de 26/08).
 *
 * Antes era uma lista corrida de linhas, cada uma com a própria data — num ano
 * com 80 lançamentos, a data era dita 80 vezes e o olho não achava "onde acaba
 * julho". É a cicatriz do IR Pro (a mesma informação repetida) aplicada ao eixo
 * do tempo, e o mesmo conserto da rubrica do dia do Calendário. A soma da
 * rubrica é só das ENTRADAS: é o número que o contador compara com o teto, e é
 * o que a DASN pede por mês.
 *
 * A ordem dos meses é a da lista que veio do banco (mais recente primeiro); os
 * lançamentos continuam sendo `LinhaDeLancamento`, a mesma peça do cliente.
 */
function ExtratoPorMes({ linhas, aoMudar }: { linhas: Lancamento[]; aoMudar: () => void }) {
  const meses = new Map<string, Lancamento[]>();
  for (const l of linhas) {
    const chave = l.data.slice(0, 7); // AAAA-MM
    if (!meses.has(chave)) meses.set(chave, []);
    meses.get(chave)!.push(l);
  }
  return (
    <div className="mt-4 space-y-6">
      {[...meses.entries()].map(([chave, doMes]) => {
        const [ano, mes] = chave.split("-");
        const entradas = doMes.filter((l) => l.natureza === "entrada").reduce((s, l) => s + l.valor, 0);
        return (
          <section key={chave} aria-labelledby={`mes-${chave}`}>
            <h3
              id={`mes-${chave}`}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-borda pb-1.5"
            >
              <span className="font-display text-base font-bold text-titulo">
                {MESES[Number(mes) - 1]} de {ano}
                <span className="ml-2 text-xs font-semibold text-cinza tabular-nums">
                  {doMes.length} {doMes.length === 1 ? "lançamento" : "lançamentos"}
                </span>
              </span>
              {entradas > 0 && (
                <span className="text-sm text-cinza tabular-nums">
                  entradas <span className="font-semibold text-tinta">{moedaDeNumero(entradas)}</span>
                </span>
              )}
            </h3>
            <ul className="mt-2 space-y-2">
              {doMes.map((l) => (
                <LinhaDeLancamento key={l.id} lancamento={l} aoMudar={aoMudar} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

const COR_DA_NATUREZA: Record<Natureza, string> = {
  entrada: "bg-chip text-link",
  compra: "bg-superficie-2 text-tinta",
  despesa: "bg-erro-fundo text-erro-tinta",
  retirada: "bg-aviso-fundo text-aviso-tinta",
};

export function LinhaDeLancamento({
  lancamento,
  aoMudar,
}: {
  lancamento: Lancamento;
  aoMudar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  // O link do comprovante nasce em DOIS toques de propósito: o bucket é privado
  // e a URL assinada só existe depois de um await — e `window.open` depois de
  // await morre no bloqueador de pop-up (a razão de a ponte /abrir ser uma
  // página). O primeiro clique busca a URL; o segundo é um <a> de verdade.
  const [linkDoAnexo, setLinkDoAnexo] = useState<string | null>(null);
  const rotulo = NATUREZAS.find((n) => n.valor === lancamento.natureza)?.rotulo ?? lancamento.natureza;

  return (
    <li className="surface p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm text-cinza tabular-nums">{dataCurta(lancamento.data)}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${COR_DA_NATUREZA[lancamento.natureza]}`}
        >
          {rotulo}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-tinta">
          {lancamento.descricao ?? ""}
          {lancamento.comprador ? (
            <span className="text-cinza"> · {lancamento.comprador}</span>
          ) : null}
        </span>
        {!lancamento.recebido && (
          <span className="text-xs font-semibold text-alerta-tinta">a receber</span>
        )}
        <span className="font-display text-sm font-bold text-titulo tabular-nums">
          {moedaDeNumero(lancamento.valor)}
        </span>
        <span className="flex shrink-0 gap-3 text-xs">
          {lancamento.anexo_path &&
            (linkDoAnexo ? (
              <a
                href={linkDoAnexo}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-link underline"
              >
                Abrir comprovante<span className="sr-only"> (abre em outra aba)</span> ↗
              </a>
            ) : (
              <button
                type="button"
                className="font-semibold text-link"
                onClick={async () => {
                  const u = await urlDoComprovante(lancamento.anexo_path!);
                  if (u) setLinkDoAnexo(u);
                  else setRecado("Não deu para abrir o comprovante agora.");
                }}
              >
                Comprovante
              </button>
            ))}
          <button type="button" className="font-semibold text-link" onClick={() => setEditando((v) => !v)}>
            {editando ? "Fechar" : "Editar"}
          </button>
          {excluindo ? (
            <>
              <button
                type="button"
                className="font-semibold text-erro-tinta underline"
                onClick={async () => {
                  const r = await excluirLancamento(lancamento.id);
                  if (!r.ok) setRecado(r.mensagem);
                  else aoMudar();
                }}
              >
                Excluir mesmo
              </button>
              <button type="button" className="font-semibold text-link" onClick={() => setExcluindo(false)}>
                Não
              </button>
            </>
          ) : (
            <button type="button" className="font-semibold text-erro-tinta" onClick={() => setExcluindo(true)}>
              Excluir
            </button>
          )}
        </span>
      </div>
      {recado && <p className="mt-2 text-sm font-semibold text-erro-tinta">{recado}</p>}
      {editando && (
        <FormularioDeLancamento
          lancamento={lancamento}
          aoSalvar={async (d) => {
            const r = await atualizarLancamento(lancamento.id, d);
            return r.ok ? { ok: true, id: lancamento.id } : r;
          }}
          aoConcluir={(aviso) => {
            setEditando(false);
            setRecado(aviso ?? null);
            aoMudar();
          }}
        />
      )}
    </li>
  );
}

/**
 * Novo e edição no MESMO formulário: a RPC de edição reescreve o registro inteiro.
 *
 * O comprovante sobe DEPOIS do salvamento, porque o caminho no bucket nasce do
 * id do lançamento (`mei_definir_anexo`) — daí `aoSalvar` devolver o id, e
 * `aoConcluir` existir à parte: se o arquivo falhar, o lançamento já está salvo
 * e o aviso diz isso, em vez de um "falhou" que faria o usuário lançar de novo.
 */
export function FormularioDeLancamento({
  lancamento,
  aoSalvar,
  aoConcluir,
}: {
  lancamento?: Lancamento;
  aoSalvar: (d: DadosDoLancamento) => Promise<{ ok: boolean; mensagem?: string; id?: string }>;
  aoConcluir: (aviso?: string) => void;
}) {
  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [data, setData] = useState(lancamento?.data ?? hoje);
  const [natureza, setNatureza] = useState<Natureza>(lancamento?.natureza ?? "entrada");
  const [valor, setValor] = useState(() => moedaDeNumero(lancamento?.valor ?? null));
  const [descricao, setDescricao] = useState(lancamento?.descricao ?? "");
  const [comprador, setComprador] = useState(lancamento?.comprador ?? "");
  const [forma, setForma] = useState(lancamento?.forma_pagamento ?? "");
  const [setor, setSetor] = useState(lancamento?.setor ?? "");
  const [recebido, setRecebido] = useState(lancamento?.recebido ?? true);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [tirarAnexo, setTirarAnexo] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  return (
    <form
      className="mt-3 space-y-3 rounded-xl bg-superficie-2 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setRecado(null);
        const moeda = mascararMoeda(valor);
        if (moeda.numero === null || moeda.numero <= 0) {
          return setRecado("O valor precisa ser maior que zero.");
        }
        if (!data) return setRecado("O lançamento precisa de data.");
        setSalvando(true);
        const r = await aoSalvar({
          data,
          natureza,
          valor: moeda.numero,
          descricao: descricao.trim() || null,
          comprador: comprador.trim() || null,
          forma: (forma || null) as DadosDoLancamento["forma"],
          setor: (setor || null) as DadosDoLancamento["setor"],
          recebido,
        });
        if (!r.ok) {
          setSalvando(false);
          return setRecado(r.mensagem ?? "Não deu para salvar agora.");
        }
        let aviso: string | undefined;
        if (arquivo && r.id) {
          const a = await anexarComprovante(r.id, arquivo);
          if (!a.ok) {
            aviso = `O lançamento foi salvo, mas o comprovante não subiu (${a.mensagem}) — abra o Editar para anexar de novo.`;
          }
        } else if (tirarAnexo && r.id) {
          const a = await removerComprovante(r.id);
          if (!a.ok) aviso = "O lançamento foi salvo, mas o comprovante antigo não saiu — tente de novo pelo Editar.";
        }
        setSalvando(false);
        aoConcluir(aviso);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Data</span>
          <input
            className={CAMPO}
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Tipo</span>
          <select
            className={CAMPO}
            value={natureza}
            onChange={(e) => setNatureza(e.target.value as Natureza)}
          >
            {NATUREZAS.map((n) => (
              <option key={n.valor} value={n.valor}>
                {n.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Valor</span>
          <input
            className={CAMPO}
            inputMode="numeric"
            placeholder="R$ 0,00"
            value={valor}
            onChange={(e) => setValor(mascararMoeda(e.target.value).texto)}
          />
        </label>
        <label className="flex items-end gap-2 pb-2 text-sm text-tinta">
          <input
            type="checkbox"
            className="size-4"
            checked={recebido}
            onChange={(e) => setRecebido(e.target.checked)}
          />
          {natureza === "entrada" ? "Já recebido" : "Já pago"}
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Descrição <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <input
            className={CAMPO}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="produto ou serviço"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Comprador <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <input className={CAMPO} value={comprador} onChange={(e) => setComprador(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Pagamento <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <select className={CAMPO} value={forma} onChange={(e) => setForma(e.target.value)}>
            <option value="">—</option>
            {FORMAS.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.rotulo}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Setor <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <select className={CAMPO} value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">—</option>
            {SETORES.map((s) => (
              <option key={s.valor} value={s.valor}>
                {s.rotulo}
              </option>
            ))}
          </select>
        </label>
        <div className="sm:col-span-3">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-titulo">
              Comprovante <span className="font-normal text-cinza">(opcional — imagem ou PDF, até 10 MB)</span>
            </span>
            <input
              type="file"
              accept={ACEITA_ANEXO}
              onChange={(e) => {
                setArquivo(e.target.files?.[0] ?? null);
                setTirarAnexo(false);
              }}
              className="block w-full text-sm text-cinza file:mr-3 file:rounded-full file:border file:border-borda file:bg-superficie file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-link"
            />
          </label>
          {lancamento?.anexo_path && !arquivo && (
            <p className="mt-1 text-xs text-cinza">
              {tirarAnexo ? (
                <>
                  O comprovante atual será removido ao salvar.{" "}
                  <button type="button" className="font-semibold text-link" onClick={() => setTirarAnexo(false)}>
                    Manter
                  </button>
                </>
              ) : (
                <>
                  Já existe um comprovante — escolher um arquivo o substitui.{" "}
                  <button type="button" className="font-semibold text-erro-tinta" onClick={() => setTirarAnexo(true)}>
                    Remover comprovante
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
      {recado && <p className="text-sm font-semibold text-erro-tinta">{recado}</p>}
      <button type="submit" className="pill-cta" disabled={salvando}>
        {salvando ? "Salvando…" : lancamento ? "Salvar lançamento" : "Lançar"}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Peças compartilhadas
// ---------------------------------------------------------------------------
/**
 * O cabeçalho das duas telas da gestão — agora a `Cabine` navy da casa
 * (redesenho de 26/08). Antes era `h1` + linha cinza + "Voltar ao MEI PRO" em
 * botão de contorno; a captura do antes mostra o mesmo diagnóstico das outras
 * ferramentas: nenhuma peça do hub em uso. A trilha substitui o botão de volta,
 * e `fatos`/`acao` são de quem chama — a lista sabe quantos clientes tem, o
 * livro sabe quantos lançamentos.
 */
function Cabecalho({
  titulo,
  sub,
  rotulo,
  volta,
  fatos,
  acao,
  children,
}: {
  titulo: string;
  sub?: string;
  rotulo?: string;
  volta?: { para: string; rotulo: string };
  fatos?: React.ReactNode;
  acao?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <>
      <Cabine
        trilha={volta ?? { para: "/ferramenta/mei-pro", rotulo: "MEI PRO" }}
        rotulo={rotulo}
        titulo={titulo}
        descricao={sub ?? "Cadastre os seus clientes MEI e escriture o livro-caixa de cada um."}
        fatos={fatos}
        acao={acao}
      />
      {children}
    </>
  );
}

/**
 * A barra do teto. As cores são semânticas por FAIXA: até 80% é neutro, de 80%
 * a 100% é atenção, acima estourou — os mesmos degraus que o conteúdo do MEI
 * ensina ("Ultrapassou o limite?" e o desenquadramento).
 */
export function BarraDoTeto({
  rotulo,
  valor,
  teto,
  motivo,
}: {
  rotulo: string;
  valor: number;
  teto: number;
  /**
   * Por que o teto é ESTE. Teto menor sem explicação parece defeito da tela: o
   * contador conhece o número de cabeça (81 mil) e, ao ver 47.250, precisa
   * saber que é o proporcional da abertura — não um erro do hub.
   */
  motivo?: string | null;
}) {
  const pct = teto > 0 ? (valor / teto) * 100 : 0;
  const cor = pct >= 100 ? "bg-erro-tinta" : pct >= 80 ? "bg-laranja" : "bg-azul-medio";
  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-cinza">{rotulo}</span>
        <span className="font-semibold text-tinta tabular-nums">
          {moedaDeNumero(valor)} <span className="font-normal text-cinza">de {moedaDeNumero(teto)}</span>
        </span>
      </div>
      {motivo && <p className="mt-0.5 text-right text-xs text-cinza">{motivo}</p>}
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-superficie-2">
        <div
          className={`h-full rounded-full ${cor}`}
          style={{ width: `${Math.min(100, pct)}%` }}
          role="progressbar"
          aria-label={rotulo}
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

export function dataCurta(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}


/**
 * O popup do convite (pedido de Victor, 20/08): depois de cadastrar, pergunta
 * se o cliente deve ter acesso à plataforma. "Sim" manda o e-mail pela Edge
 * Function `convidar-cliente-mei` — o link nunca passa por esta tela, só o
 * desfecho. `<dialog>` nativo, como o ModalVigencias: Esc, foco e backdrop de
 * graça.
 */
function DialogoDeConvite({ cliente, aoFechar }: { cliente: ClienteMei; aoFechar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDoConvite | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const TEXTO: Record<ResultadoDoConvite, string> = {
    convite_enviado: `Convite enviado para ${cliente.email}. O cliente cria a senha pelo link do e-mail e vê somente o livro-caixa dele — nada dos seus outros clientes ou dos cursos.`,
    redefinicao_enviada: `Esse e-mail já tinha uma conta começada e não confirmada — mandamos um link para ${cliente.email} criar a senha e concluir.`,
    ja_tem_acesso: "Esse cliente já tem acesso: ele entra na plataforma com o e-mail dele, em Entrar.",
  };

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current && !enviando) aoFechar();
      }}
      aria-labelledby="titulo-convite"
      className="m-auto w-[min(94vw,28rem)] rounded-[1.125rem] bg-superficie p-6 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      {resultado ? (
        <>
          <h2 id="titulo-convite" className="font-display text-lg font-bold text-titulo">
            {resultado === "ja_tem_acesso" ? "Este cliente já entra sozinho" : "Convite a caminho"}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{TEXTO[resultado]}</p>
          <button type="button" className="pill-cta mt-5" onClick={aoFechar}>
            Entendi
          </button>
        </>
      ) : (
        <>
          <h2 id="titulo-convite" className="font-display text-lg font-bold text-titulo">
            {cliente.nome ?? "O cliente"} deve ter acesso à plataforma?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-cinza">
            Mandamos um convite para <strong className="text-tinta">{cliente.email}</strong>. Ele
            cria a própria senha e vê <strong className="text-tinta">somente o livro-caixa dele</strong> —
            os lançamentos que você ou ele fizerem.
          </p>
          {erro && <p className="mt-3 text-sm font-semibold text-erro-tinta">{erro}</p>}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              className="pill-cta"
              disabled={enviando}
              onClick={async () => {
                setErro(null);
                setEnviando(true);
                const r = await convidarCliente(cliente.cliente_id);
                setEnviando(false);
                if (r.ok) setResultado(r.res);
                else setErro(r.mensagem);
              }}
            >
              {enviando ? "Enviando…" : "Sim, enviar convite"}
            </button>
            <button type="button" className="btn-contorno" disabled={enviando} onClick={aoFechar}>
              Agora não
            </button>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-cinza">
            Dá para convidar depois, pelo "Convidar por e-mail" na lista de clientes.
          </p>
        </>
      )}
    </dialog>
  );
}
